<template>
  <CModal
    :show.sync="showModal"
    :color="color"
    class="basic-modal-color"
    size="lg"
  >
    <template #header>
      <CRow style="width: 100%;">
        <CCol md="10">
          <h5 class="modal-title">Enviar Excel por correo</h5>
        </CCol>
      </CRow>
      <CButtonClose @click="showModal = false" />
    </template>

    <CRow>
      <CCol md="12">
        <label class="form-label">Destinatarios</label>
        <div class="chips-wrapper" @click="focusEmailInput">
          <span v-for="(recipient, index) in recipients" :key="recipient" class="chip-item">
            {{ recipient }}
            <button type="button" class="chip-remove" @click.stop="removeRecipient(index)">x</button>
          </span>

          <input
            ref="emailInput"
            v-model="inputEmail"
            class="chips-input"
            type="text"
            @keydown="handleInputKeydown"
            @focus="isInputFocused = true"
            @blur="onInputBlur"
          />
        </div>

        <div v-if="showSuggestion" class="suggestion-box">
          <button type="button" class="suggestion-item" @mousedown.prevent="addSuggestion">
            Agregar destinatario: <strong>{{ emailSuggestion }}</strong>
          </button>
        </div>

        <small v-if="inputError" class="text-danger d-block mt-1">{{ inputError }}</small>
      </CCol>
    </CRow>

    <CRow class="mt-3">
      <CCol md="12">
        <CInput
          label="Asunto"
          type="text"
          :value="subject"
          disabled
        />
      </CCol>
    </CRow>

    <CRow class="mt-2">
      <CCol md="12" v-if="lastHistory">
        <div class="history-box">
          <div>
            <strong>Ultimo envio:</strong>
            {{ formatDate(lastHistory.sent_at || lastHistory.created_at) }}
          </div>
          <div>
            <strong>Estado:</strong>
            <CBadge :color="lastHistory.status === 'success' ? 'success' : 'danger'">
              {{ lastHistory.status === 'success' ? 'exitoso' : 'fallido' }}
            </CBadge>
          </div>
          <div v-if="lastHistory.error_message" class="text-danger small mt-1">
            {{ lastHistory.error_message }}
          </div>
        </div>
      </CCol>
    </CRow>

    <template #footer>
      <CButton color="secondary" @click="closeModal" :disabled="isSending">Cancelar</CButton>
      <CButton :color="color" @click="sendExcel" :disabled="isSending">
        <span v-if="isSending">Enviando...</span>
        <span v-else>Enviar</span>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import store from '@/store';
import ws from '@/services/events_guests';

export default {
  name: 'SendGuestsExcelModal',
  data() {
    return {
      showModal: false,
      color: 'info',
      event: null,
      filters: {},
      inputEmail: '',
      recipients: [],
      inputError: '',
      isSending: false,
      lastHistory: null,
      isInputFocused: false,
    }
  },
  computed: {
    subject() {
      if (!this.event || !this.event.name) return 'Lista de invitados';
      return `Lista de invitados - ${this.event.name}`;
    },
    storageKey() {
      const domain = localStorage.getItem('domain') || 'default';
      const token = localStorage.getItem('token') || 'anonymous';
      const eventId = this.event && this.event.id ? this.event.id : '0';
      return `confirmame:excel-email:${domain}:${token}:${eventId}`;
    },
    emailSuggestion() {
      const value = (this.inputEmail || '').trim().toLowerCase();
      if (!value || /[\s,;]+/.test(value)) {
        return '';
      }

      if (!this.isValidEmail(value)) {
        return '';
      }

      if (this.recipients.includes(value)) {
        return '';
      }

      return value;
    },
    showSuggestion() {
      return this.isInputFocused && !!this.emailSuggestion;
    }
  },
  methods: {
    async showSendModal(event, filters) {
      this.showModal = true;
      this.event = event;
      this.filters = { ...(filters || {}), event_id: event.id };
      this.inputEmail = '';
      this.inputError = '';
      this.isSending = false;
      this.lastHistory = null;
      this.recipients = [];
      this.isInputFocused = false;

      this.preloadRecipients();
      await this.loadLastHistory();
    },

    preloadRecipients() {
      const raw = localStorage.getItem(this.storageKey);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length > 0) {
            this.recipients = parsed;
            return;
          }
        } catch (e) {
        }
      }

      if (this.event && this.event.planner_email) {
        this.recipients = [this.event.planner_email];
      }
    },

    focusEmailInput() {
      if (this.$refs.emailInput) {
        this.$refs.emailInput.focus();
      }
    },

    addRecipientFromInput() {
      const value = (this.inputEmail || '').trim();
      if (!value) {
        this.inputEmail = '';
        return;
      }

      const tokens = value
        .split(/[\s,;]+/)
        .map(v => v.trim().toLowerCase())
        .filter(v => v !== '');

      let hasInvalid = false;
      tokens.forEach((email) => {
        if (!this.isValidEmail(email)) {
          hasInvalid = true;
          return;
        }

        if (!this.recipients.includes(email)) {
          this.recipients.push(email);
        }
      });

      this.inputError = hasInvalid ? 'Uno o mas correos no tienen formato valido.' : '';
      this.inputEmail = '';
    },

    addSuggestion() {
      if (!this.emailSuggestion) {
        return;
      }

      this.recipients.push(this.emailSuggestion);
      this.inputEmail = '';
      this.inputError = '';
      this.focusEmailInput();
    },

    onInputBlur() {
      setTimeout(() => {
        this.isInputFocused = false;
      }, 150);
    },

    handleInputKeydown(event) {
      if (event.key === 'Enter' || event.key === 'Tab' || event.key === ',') {
        event.preventDefault();

        if (this.emailSuggestion) {
          this.addSuggestion();
          return;
        }

        this.addRecipientFromInput();
      }
    },

    removeRecipient(index) {
      this.recipients.splice(index, 1);
    },

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },

    async loadLastHistory() {
      try {
        const response = await ws.lastExcelEmailHistory({ event_id: this.event.id });
        if (response.type === 'success') {
          this.lastHistory = response.data || null;
        }
      } catch (e) {
        this.lastHistory = null;
      }
    },

    async sendExcel() {
      this.addRecipientFromInput();

      if (!this.recipients.length) {
        this.showToast('error', 'Debes indicar al menos un correo valido.');
        return;
      }

      this.isSending = true;
      this.loading();

      try {
        const payload = {
          ...this.filters,
          event_id: this.event.id,
          recipients: this.recipients,
        };

        const response = await ws.sendExcelEmail(payload);

        if (response.type === 'success') {
          localStorage.setItem(this.storageKey, JSON.stringify(this.recipients));
          this.lastHistory = response.data || this.lastHistory;
          await this.loadLastHistory();
          this.showToast('success', 'Excel enviado correctamente.');
          this.closeModal();
        } else {
          await this.loadLastHistory();
          this.showToast('error', 'No se pudo enviar el Excel. Intenta nuevamente.');
        }
      } catch (e) {
        await this.loadLastHistory();
        this.showToast('error', 'No se pudo enviar el Excel. Intenta nuevamente.');
      } finally {
        this.isSending = false;
        this.loaded();
      }
    },

    formatDate(value) {
      if (!value) return '-';
      const date = new Date(value.replace(' ', 'T'));
      if (Number.isNaN(date.getTime())) return value;

      const dd = `${date.getDate()}`.padStart(2, '0');
      const mm = `${date.getMonth() + 1}`.padStart(2, '0');
      const yyyy = date.getFullYear();
      const hh = `${date.getHours()}`.padStart(2, '0');
      const min = `${date.getMinutes()}`.padStart(2, '0');
      return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    },

    closeModal() {
      this.showModal = false;
    },

    showToast(type, message) {
      store.commit('toast', { color: type, message });
    },
    loading() {
      store.commit('loading');
    },
    loaded() {
      store.commit('loaded');
    },
  }
}
</script>

<style scoped>
.basic-modal-color .modal-header {
  background-color: #668269 !important;
}

.chips-wrapper {
  min-height: 44px;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 6px;
  background: #fff;
}

.chip-item {
  display: inline-flex;
  align-items: center;
  background: #e8f0fe;
  color: #1f3b82;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
}

.chip-remove {
  border: 0;
  background: transparent;
  margin-left: 6px;
  color: #1f3b82;
  font-weight: bold;
  cursor: pointer;
}

.chips-input {
  border: 0;
  outline: none;
  min-width: 220px;
  flex: 1;
  font-size: 14px;
}

.suggestion-box {
  border: 1px solid #ced4da;
  border-top: 0;
  border-radius: 0 0 0.25rem 0.25rem;
  background: #fff;
  margin-top: -2px;
}

.suggestion-item {
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  padding: 8px 10px;
  font-size: 13px;
  cursor: pointer;
}

.suggestion-item:hover {
  background: #f1f3f5;
}

.history-box {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 10px;
  font-size: 13px;
}
</style>
